import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    LinearProgress,
    Chip
} from '@mui/material';
import { FaUserGraduate, FaTasks, FaChartBar, FaClock, FaCheckCircle } from 'react-icons/fa';
import Header from '../../components/header';
import Footer from '../../components/footer';
import DataTable from '../../components/DataTable';
import teacherService from '../../services/teacherService';
import courseService from '../../services/courseService';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import StudentProfileModal from '../../components/StudentProfileModal';

// --- Stats Card Component ---
const StatsCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
            <Box sx={{
                mr: 2,
                p: 2,
                borderRadius: '50%',
                backgroundColor: `${color}20`,
                color: color,
                display: 'flex'
            }}>
                {icon}
            </Box>
            <Box>
                <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="textSecondary">
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </CardContent>
    </Card>
);

// Course analytics card
const CourseAnalyticsCard = ({ overview }) => {
    if (!overview) return null;

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaChartBar /> Course Analytics: {overview.course_title}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                    <Box>
                        <Typography variant="body2" color="textSecondary">Active Students</Typography>
                        <Typography variant="h5" fontWeight="bold">{overview.total_students}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box>
                        <Typography variant="body2" color="textSecondary">Class Accuracy</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h5" fontWeight="bold" color={overview.avg_accuracy >= 70 ? 'success.main' : overview.avg_accuracy >= 50 ? 'warning.main' : 'error.main'}>
                                {overview.avg_accuracy}%
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={overview.avg_accuracy}
                                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                color={overview.avg_accuracy >= 70 ? 'success' : overview.avg_accuracy >= 50 ? 'warning' : 'error'}
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box>
                        <Typography variant="body2" color="textSecondary">Avg. Time Spent</Typography>
                        <Typography variant="h5" fontWeight="bold">{overview.avg_time_minutes} min</Typography>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

const TeacherDashboard = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total_students: 0, active_assignments: 0 });
    const [students, setStudents] = useState([]);

    // Course analytics state
    const [selectedCourse, setSelectedCourse] = useState('');
    const [courseOverview, setCourseOverview] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(false);

    // Available courses - fetched from API
    const [courses, setCourses] = useState([]);

    const fetchAllData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsData, studentsData, levelsData] = await Promise.all([
                teacherService.getStats().catch(() => ({ total_students: 0, active_assignments: 0 })),
                teacherService.getMyStudents().catch(() => []),
                courseService.getAllLevels().catch(() => [])
            ]);
            setStats(statsData);
            setStudents(studentsData);
            // Extract course titles from levels data
            // The title field contains the full course name like "Preparatory level – A0"
            const courseTitles = levelsData.map(level => level.title).filter(Boolean);
            setCourses(courseTitles);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Fetch course overview when course is selected
    useEffect(() => {
        if (selectedCourse) {
            setLoadingOverview(true);
            teacherService.getCourseOverview(selectedCourse, 7)
                .then(setCourseOverview)
                .catch(err => {
                    console.error("Failed to load course overview", err);
                    setCourseOverview(null);
                })
                .finally(() => setLoadingOverview(false));
        } else {
            setCourseOverview(null);
        }
    }, [selectedCourse]);

    const [selectedStudentId, setSelectedStudentId] = useState(null);

    const handleViewDetails = (studentId) => {
        setSelectedStudentId(studentId);
    };

    const handleCloseModal = () => {
        setSelectedStudentId(null);
    };

    const studentColumns = [
        { id: 'name', label: 'Name', render: (row) => `${row.user.first_name} ${row.user.last_name}` },
        { id: 'email', label: 'Email', render: (row) => row.user.email },
        {
            id: 'progress',
            label: 'Progress',
            render: (row) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <LinearProgress
                        variant="determinate"
                        value={row.progress}
                        sx={{ width: 60, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="body2">{row.progress}%</Typography>
                </Box>
            )
        },
        {
            id: 'status',
            label: 'Status',
            render: (row) => (
                <Chip
                    label={row.user.is_active ? 'Active' : 'Inactive'}
                    color={row.user.is_active ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            id: 'actions', label: 'Actions', render: (row) => (
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleViewDetails(row.user.id)}
                    startIcon={<FaChartBar size={12} />}
                >
                    Analytics
                </Button>
            )
        }
    ];

    if (!user || user.role !== 'teacher') {
        return <div className="page-wrapper"><Header /><div className="container py-5">Access Denied</div><Footer /></div>;
    }

    return (
        <div className="page-wrapper">
            <Header />
            <div className="wpo-team-section section-padding" style={{ backgroundColor: '#f5f5f5', minHeight: '80vh' }}>
                <div className="container">
                    <div className="row mb-4">
                        <div className="col-12">
                            <Typography variant="h4" gutterBottom>
                                {t('dashboard.teacherWelcome', { name: user.first_name || 'Teacher', defaultValue: 'Teacher Dashboard' })}
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                Track your students' progress and performance analytics.
                            </Typography>
                        </div>
                    </div>

                    {/* Stats Overview */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatsCard
                                title="Total Students"
                                value={stats.total_students}
                                icon={<FaUserGraduate size={24} />}
                                color="#1976d2"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatsCard
                                title="Active This Week"
                                value={stats.active_this_week || 0}
                                icon={<FaTasks size={24} />}
                                color="#2e7d32"
                                subtitle={`${stats.total_questions_answered || 0} questions answered`}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatsCard
                                title="Avg. Accuracy"
                                value={`${stats.global_avg_accuracy || 0}%`}
                                icon={<FaCheckCircle size={24} />}
                                color="#ed6c02"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatsCard
                                title="Total Time"
                                value={`${stats.global_avg_time_minutes || 0} min`}
                                icon={<FaClock size={24} />}
                                color="#9c27b0"
                            />
                        </Grid>
                    </Grid>

                    {/* Course Selector */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Course Analytics
                        </Typography>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Select Course</InputLabel>
                            <Select
                                value={selectedCourse}
                                label="Select Course"
                                onChange={(e) => setSelectedCourse(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select a course...</em>
                                </MenuItem>
                                {courses.map(course => (
                                    <MenuItem key={course} value={course}>{course}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {loadingOverview && <LinearProgress sx={{ mb: 2 }} />}

                        {courseOverview && <CourseAnalyticsCard overview={courseOverview} />}
                    </Paper>

                    {/* Main Content - Student List */}
                    <Paper sx={{ width: '100%', p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            My Students
                        </Typography>
                        <DataTable
                            columns={studentColumns}
                            data={students}
                            loading={loading}
                            count={students.length}
                            page={1}
                        />
                    </Paper>
                </div>
            </div>
            <Footer />

            <StudentProfileModal
                open={!!selectedStudentId}
                onClose={handleCloseModal}
                studentId={selectedStudentId}
                fetchProgressFn={teacherService.getStudentProgress}
            />
        </div>
    );
};

export default TeacherDashboard;
